/* =============================================================================
   Mobile sticky CTA bar  <mobile-cta-bar>
   -----------------------------------------------------------------------------
   - Mobile only (matchMedia). Never runs on desktop.
   - Visible when the user has scrolled PAST the "start" element and has NOT yet
     reached the "end" element. Both are optional CSS selectors:
       data-start : bar appears once this element's bottom passes the top of the
                    viewport. Empty -> appears after ~half a screen of scroll.
       data-end   : bar hides once this element's top enters the viewport.
                    Empty -> stays visible to the page bottom.
   - Smoothness comes from a CSS transform transition; JS only toggles a class,
     throttled with requestAnimationFrame.
   - The dismiss (X) button hides the bar for the rest of the session.
   - The primary button (when it has data-scroll-target) smooth-scrolls to the
     form and focuses the first real field.
   ========================================================================== */

(function () {
  'use strict';

  var DISMISS_KEY = 'htoMobileBarDismissed';
  var MOBILE_MQ = '(max-width: 749px)';
  var START_FALLBACK_PX = 300; // when no start selector is given

  class MobileCtaBar extends HTMLElement {
    constructor() {
      super();
      this.passedStart = false;
      this.nearEnd = false;
      this.dismissed = false;
      this.ticking = false;
    }

    connectedCallback() {
      this.startSel = (this.dataset.start || '').trim();
      this.endSel = (this.dataset.end || '').trim();
      this.dismissible = this.dataset.dismissible === 'true';
      this.mq = window.matchMedia(MOBILE_MQ);

      this.resolveTargets();

      try {
        if (this.dismissible && sessionStorage.getItem(DISMISS_KEY) === '1') {
          this.dismissed = true;
        }
      } catch (e) {
        /* storage blocked — ignore */
      }

      var closeBtn = this.querySelector('.hto-mbar-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.dismiss());

      var primary = this.querySelector('[data-scroll-target]');
      if (primary) primary.addEventListener('click', (e) => this.scrollToTarget(e, primary));

      this._onScroll = () => this.requestUpdate();
      this._onResize = () => {
        this.resolveTargets();
        this.requestUpdate();
      };

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this._onResize);
      } else if (this.mq.addListener) {
        this.mq.addListener(this._onResize); // Safari < 14
      }

      // Initial paint: compute, then enable the slide on the next frame so the
      // very first appearance animates instead of snapping.
      this.compute();
      this.render(true);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this.mq && this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this._onResize);
      } else if (this.mq && this.mq.removeListener) {
        this.mq.removeListener(this._onResize);
      }
    }

    resolveTargets() {
      this.startEl = this.startSel ? document.querySelector(this.startSel) : null;
      this.endEl = this.endSel ? document.querySelector(this.endSel) : null;
    }

    requestUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.compute();
        this.render();
      });
    }

    compute() {
      // Passed the start element once its bottom is above the viewport top.
      if (this.startEl) {
        this.passedStart = this.startEl.getBoundingClientRect().bottom <= 0;
      } else {
        this.passedStart = (window.pageYOffset || window.scrollY || 0) > START_FALLBACK_PX;
      }

      // Reached the end element once its top has entered the viewport.
      if (this.endEl) {
        this.nearEnd = this.endEl.getBoundingClientRect().top <= window.innerHeight;
      } else {
        this.nearEnd = false;
      }
    }

    render(initial) {
      // Desktop (or above the breakpoint): fully hidden, no animation.
      if (!this.mq.matches) {
        this.classList.remove('is-visible');
        this.setAttribute('hidden', '');
        return;
      }

      // On mobile the element must be rendered (off-screen) so the transform
      // can animate; the `hidden` attribute would block that.
      this.removeAttribute('hidden');

      var show = this.passedStart && !this.nearEnd && !this.dismissed;

      if (initial) {
        // Force the off-screen start state to be committed before toggling.
        requestAnimationFrame(() => this.classList.toggle('is-visible', show));
      } else {
        this.classList.toggle('is-visible', show);
      }
    }

    dismiss() {
      this.dismissed = true;
      try {
        sessionStorage.setItem(DISMISS_KEY, '1');
      } catch (e) {
        /* storage blocked — the bar still hides for now */
      }
      this.render();
    }

    scrollToTarget(e, btn) {
      var sel = btn.getAttribute('data-scroll-target');
      var el = sel ? document.querySelector(sel) : null;
      if (!el) return; // nothing to scroll to — do nothing

      e.preventDefault();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });

      var field = el.querySelector(
        'input:not([type=hidden]):not([data-honeypot]):not([tabindex="-1"]), select, textarea'
      );
      if (field) {
        setTimeout(function () {
          try {
            field.focus({ preventScroll: true });
          } catch (err) {
            field.focus();
          }
        }, 450);
      }
    }
  }

  if (!customElements.get('mobile-cta-bar')) {
    customElements.define('mobile-cta-bar', MobileCtaBar);
  }
})();
